import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, Info, Settings } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next'; // ADD THIS IMPORT
import { Category } from '../../shared/hooks/useCategorySelection';
import { Service, useServiceSelection } from '../../shared/hooks/useServiceSelection';
import { useLayoutContext } from '../../shared/hooks/useLayoutContext';
import FooterNavigation from '../../shared/components/Navigation';
import Pagination from '../../shared/components/Pagination';
import { ServiceSelectionSkeleton } from '../../layouts/centered/components/ServiceCardSkeleton';
import { ServiceModal } from '../../layouts/centered/components/ServiceModal';
import ServiceDisplay from '../../layouts/centered/components/ServiceDisplay';
import ImmersiveServiceDisplay from '../../layouts/immersive/components/ImmersiveServiceDisplay';

// New component for no services message
const NoServicesMessage = ({ colors = {} }) => {
  const { t } = useTranslation(); // ADD THIS LINE

  // Default colors if not provided
  //@ts-expect-error
  const titleText = colors.titleText || '#DDB889';
  //@ts-expect-error
  const cardBackground = colors.cardBackground || 'rgba(0, 0, 0, 0.95)';
  //@ts-expect-error
  const cardBorder = colors.cardBorder || '#DDB88919';
  //@ts-expect-error
  const cardShadow = colors.cardShadow || 'rgba(221,184,137,0.07)';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="lp-flex lp-flex-col lp-items-center lp-justify-center lp-min-h-[400px] lp-text-center lp-p-8"
    >
      <div
        className="lp-backdrop-blur-sm lp-border lp-rounded-2xl lp-p-8 lp-max-w-lg
          lp-transition-all lp-duration-700"
        style={{
          backgroundColor: cardBackground,
          borderColor: `${cardBorder}`,
          boxShadow: `0 0 15px ${cardShadow}`
        }}
      >
        <CheckCircle2
          className="lp-w-12 lp-h-12 lp-mx-auto lp-mb-4"
          style={{ color: titleText }}
        />
        <h3
          className="lp-text-xl lp-font-medium lp-mb-3"
          style={{ color: titleText }}
        >
          {t('services.noServicesAvailable')} {/* CHANGED: "No Services Available" */}
        </h3>
        <p className="lp-text-gray-300 lp-mb-4">
          {t('services.noServicesDescription')} {/* CHANGED: "There are currently no services available..." */}
        </p>
        <div
          className="lp-flex lp-items-center lp-justify-center lp-gap-2 lp-text-sm"
          style={{ color: `${titleText}CC` }}
        >
          <Info size={16} />
          <span>{t('services.checkBackLater')}</span> {/* CHANGED: "Please wait until services become available" */}
        </div>
      </div>
    </motion.div>
  );
};

interface ServiceSelectionProps {
  selectedServices: Service[];
  widgetConfiguration: any;
  selectedCategories: Category[];
  setSelectedServices: React.Dispatch<React.SetStateAction<Service[]>>;
  onNext: () => void;
  onPrev: () => void;
  nodeData?: any;
}

const ServiceSelection: React.FC<ServiceSelectionProps> = ({
  selectedServices,
  setSelectedServices,
  widgetConfiguration,
  selectedCategories,
  onNext,
  onPrev,
  nodeData = null,
}) => {
  const { t } = useTranslation(); // ADD THIS LINE
  const { layoutType } = useLayoutContext();
  // Set default nodeData if none is provided
  const defaultNodeData = {
    label: t('booking.steps.service'), // CHANGED: "Services"
    settings: {
      displayMode: "cards",
      showPrices: true,
      showImages: true,
      showDuration: true,
      showDescriptions: true,
      displayStyle: "card",
      itemsPerRow: 3,
      numberRows: 2,
      colors: {
        cardBackground: "#000000F2",
        cardBorder: "#DDB88919",
        cardBorderSelected: "#22c55e49",
        cardShadow: "rgba(221,184,137,0.07)",
        ardShadowSelected: "rgba(34,197,94,0.25)",
        titleText: "#DDB889",
        subtitleText: "#9CA3AF",
        infoText: "#DDB889",
        iconColor: "#DDB889",
        selectionBackground: "#22c55e",
        selectionIcon: "#FFFFFF",
        badgeBackground: "#000000F2",
        badgeBorder: "#DDB88919",
        buttonBackground: "#DDB889",
        buttonText: "#000000",
        placeholderGradientFrom: "#DDB88933",
        placeholderGradientTo: "#DDB8890D",
        placeholderIconBackground: "#DDB88933"
      }
    }
  };

  // Use nodeData if provided, otherwise use default
  const effectiveNodeData = {
  label: nodeData?.label || defaultNodeData.label,
  settings: {
    ...defaultNodeData.settings,
    ...(nodeData?.settings || {}),
    colors: {
      ...defaultNodeData.settings.colors,
      ...(nodeData?.settings?.colors || {})
    }
  }
};

  // Calculate items per page based on nodeData settings
  // itemsPerRow × numberRows determines total items per page
  const itemsPerPage = layoutType === 'immersive'
    ? 4
    : (effectiveNodeData?.settings?.itemsPerRow || 2) * (effectiveNodeData?.settings?.numberRows || 2);

  const {
    expandedDescriptionId,
    queryResult,
    toggleService,
    handlePageChange,
    setExpandedDescriptionId,
    formatDuration,
    shouldShowReadMore,
  } = useServiceSelection({
    selectedServices,
    setSelectedServices,
    itemsPerPage,
    selectedCategories
  });

  const pageInfo = queryResult.data?.domainServices.pageInfo;

  if (layoutType === 'immersive') {
    return (
      <ImmersiveServiceDisplay
        services={queryResult.data?.domainServices.edges.map(({ node }: { node: Service }) => node) || []}
        selectedServices={selectedServices}
        onToggleService={toggleService}
        onNext={onNext}
        formatDuration={formatDuration}
        currency={widgetConfiguration.currency}
        hasNextPage={pageInfo?.hasNextPage ?? false}
        hasPrevPage={pageInfo?.hasPreviousPage ?? false}
        onNextPage={() => handlePageChange('next')}
        onPrevPage={() => handlePageChange('previous')}
      />
    );
  }

  // Check if services exist
  const hasServices = queryResult.data?.domainServices.edges.length > 0;

  return (
    <>
      <motion.div
        className="lp-flex lp-flex-col lp-relative lp-overflow-hidden lp-rounded-2xl"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      >
        <Pagination
            handlePageChange={handlePageChange}
            pageInfo={queryResult.data?.domainServices.pageInfo || { hasNextPage: false, hasPreviousPage: false }}
            title={t('services.selectService')}
            Icon={Settings}
            showNavigation={true}
          />
        <div className="lp-w-full lp-relative lp-z-10">
          <AnimatePresence mode="wait">
            {queryResult.fetching && !queryResult.data ? (
              <motion.div
                key="skeleton"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
              >
                <ServiceSelectionSkeleton />
              </motion.div>
            ) : hasServices ? (
              <motion.div
                key="content"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <ServiceDisplay
                  services={queryResult.data?.domainServices.edges.map(({ node }: { node: Service }) => node) || []}
                  selectedServices={selectedServices}
                  onToggle={toggleService}
                  formatDuration={formatDuration}
                  currency={widgetConfiguration.currency}
                  shouldShowReadMore={shouldShowReadMore}
                  onReadMore={(id: string) => setExpandedDescriptionId(id)}
                  nodeData={effectiveNodeData}
                />
              </motion.div>
            ) : (
              <motion.div
                key="no-services"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <NoServicesMessage colors={effectiveNodeData.settings.colors} />
              </motion.div>
            )}
          </AnimatePresence>

          <div className="lp-mt-8" />
        </div>

        <AnimatePresence>
          {expandedDescriptionId && queryResult.data && (
            <ServiceModal
              service={queryResult.data.domainServices.edges.find(
                ({ node }: { node: Service }) => node.id === expandedDescriptionId
              )?.node}
              currency={widgetConfiguration.currency}
              onClose={() => setExpandedDescriptionId(null)}
              formatDuration={formatDuration}
            />
          )}
        </AnimatePresence>
      </motion.div>
      {hasServices ? (
        <FooterNavigation
          onPrev={widgetConfiguration.isLocationsActivated ? onPrev : null}
          onNext={onNext}
          disabled={selectedServices.length === 0}
        />
      ) : null}
    </>
  );
};

export default ServiceSelection;
